import { yupResolver } from '@hookform/resolvers/yup';
import { message, Modal } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import {
  type MarketplaceRootData,
  configurationSchema,
  contentSchema,
  creationSchema,
  marketplaceRootDefaultValues,
  marketplaceRootValidationSchema,
  marketplaceStepFields,
} from '../schemas/rootSchema';
import { MarketplaceItemStatus } from '../types/types';
import { baseProps } from '../utils/modalUtils';
import { toMarketplaceFormValues } from '../utils/toMarketplaceFormValues';
import {
  useMarketplaceActivateProduct,
  useMarketplaceCreateProduct,
  useMarketplaceProduct,
  useMarketplaceUpdateProduct,
  useMarketplaceUpdatePublishedProduct,
} from './useMarketplace';
import { useMarketplaceProperties } from './useMarketplaceOptions';

type MarketplaceFormValidated = yup.InferType<
  typeof marketplaceRootValidationSchema
>;

export function useMarketplaceForm() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;
  const marketplaceId = id ? Number(id) : undefined;
  const [currentStep, setCurrentStep] = useState(0);
  const [visitedSteps, setVisitedSteps] = useState<Set<number>>(() =>
    isEditing ? new Set([0, 1, 2, 3]) : new Set([0])
  );
  const [showUnsavedAlert, setShowUnsavedAlert] = useState(false);

  const initializedForIdRef = useRef<number | undefined>(undefined);
  const skipBlockerRef = useRef(false);

  const { product: existingProduct, isLoading: isLoadingProduct } =
    useMarketplaceProduct(marketplaceId);

  // Prefetch properties so offer blocks have them cached when rendered
  const { properties } = useMarketplaceProperties();

  const productStatus = existingProduct?.status;
  const isPublished = productStatus === MarketplaceItemStatus.Active;

  const headerInfo = useMemo(
    () => ({
      title: isEditing ? 'Editar Item' : 'Criar Item',
      subTitle: isEditing
        ? isPublished
          ? 'Edite as informações do item publicado. Alterações restritivas serão validadas.'
          : 'Edite as informações do item.'
        : 'Cadastre um novo item no marketplace.',
    }),
    [isEditing, isPublished]
  );

  const marketplaceFormMethods = useForm<MarketplaceFormValidated>({
    resolver: yupResolver(marketplaceRootValidationSchema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: marketplaceRootDefaultValues,
  });

  const { createMarketplaceProduct, isCreatingProduct } =
    useMarketplaceCreateProduct(marketplaceFormMethods);

  const { updateMarketplaceProduct, isUpdatingProduct } =
    useMarketplaceUpdateProduct(marketplaceId ?? 0, marketplaceFormMethods);

  const { updatePublishedProduct, isUpdatingPublishedProduct } =
    useMarketplaceUpdatePublishedProduct(
      marketplaceId ?? 0,
      marketplaceFormMethods
    );

  const { activateMarketplaceProduct, isActivatingProduct } =
    useMarketplaceActivateProduct(marketplaceId ?? 0);

  const isLoadingSubmit = isEditing
    ? isPublished
      ? isUpdatingPublishedProduct
      : isUpdatingProduct || isActivatingProduct
    : isCreatingProduct;

  const {
    watch,
    formState: { isDirty },
  } = marketplaceFormMethods;

  const formValues = watch();

  useEffect(() => {
    if (
      existingProduct &&
      properties &&
      initializedForIdRef.current !== marketplaceId
    ) {
      initializedForIdRef.current = marketplaceId;
      marketplaceFormMethods.reset(
        toMarketplaceFormValues(existingProduct, properties)
      );
    }
  }, [existingProduct, properties, marketplaceId, marketplaceFormMethods]);

  useEffect(() => {
    if (isDirty) {
      setShowUnsavedAlert(true);
    }
  }, [isDirty]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !skipBlockerRef.current &&
      isDirty &&
      currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (blocker.state !== 'blocked') return;

    Modal.confirm({
      title: isEditing
        ? 'Você tem alterações não salvas'
        : 'Sair sem finalizar o produto?',
      content: isEditing
        ? 'Atenção: todas as modificações feitas neste produto serão descartadas permanentemente se você sair agora. Esta ação não pode ser desfeita.'
        : 'Você começou a criar um produto mas ainda não o salvou. Se sair agora, todo o preenchimento será perdido.',
      okText: isEditing ? 'Sair e perder alterações' : 'Sair assim mesmo',
      okType: 'danger',
      cancelText: isEditing ? 'Continuar editando' : 'Voltar e continuar',
      centered: true,
      ...baseProps,
      onOk: () => {
        marketplaceFormMethods.reset({ ...marketplaceRootDefaultValues });
        blocker.proceed?.();
      },
      onCancel: () => {
        blocker.reset?.();
      },
    });
  }, [blocker, isEditing, marketplaceFormMethods]);

  const stepsCompleted = useMemo(() => {
    const validateStep = (
      schema: yup.ObjectSchema<any>,
      data: any,
      context?: Record<string, unknown>
    ) => {
      try {
        schema.validateSync(data, { context });
        return true;
      } catch {
        return false;
      }
    };

    const productType = formValues.creation?.productType;
    const isCombo = productType === 'combo';
    const isProgram = productType === 'program';

    return {
      creation: validateStep(creationSchema, formValues.creation),
      configuration: validateStep(
        configurationSchema,
        formValues.configuration
      ),
      apresentation: validateStep(contentSchema, formValues.content, {
        isCombo,
        isProgram,
      }),
      scheduling:
        productStatus === MarketplaceItemStatus.Active ||
        productStatus === MarketplaceItemStatus.Scheduled,
    };
  }, [formValues]);

  const handleChangeStep = useCallback(
    (step: number) => {
      const alreadyVisited = visitedSteps.has(step);
      setVisitedSteps(prev => new Set([...prev, step]));
      setCurrentStep(step);

      if (alreadyVisited) {
        marketplaceFormMethods.trigger(marketplaceStepFields[step]);
      }
    },
    [marketplaceFormMethods, visitedSteps]
  );

  const stepsWithErrors = useMemo(() => {
    return {
      creation: visitedSteps.has(0) && !stepsCompleted.creation,
      configuration: visitedSteps.has(1) && !stepsCompleted.configuration,
      apresentation: visitedSteps.has(2) && !stepsCompleted.apresentation,
      scheduling: visitedSteps.has(3) && !stepsCompleted.scheduling,
    };
  }, [stepsCompleted, visitedSteps]);

  const publishActionLabel =
    formValues.schedule?.publishType === 'scheduled' ? 'Agendar' : 'Publicar';

  const isPublishActionDisabled = useMemo(() => {
    return (
      !stepsCompleted.creation ||
      !stepsCompleted.configuration ||
      !stepsCompleted.apresentation
    );
  }, [stepsCompleted]);

  const getSuccessMessage = (
    intent: 'draft' | 'publish',
    publishType?: string
  ): string => {
    if (intent !== 'publish') return 'Dados salvos com sucesso!';
    return publishType === 'scheduled'
      ? 'Produto agendado com sucesso!'
      : 'Produto publicado com sucesso!';
  };

  const handleBack = useCallback(() => {
    navigate('/marketplace');
  }, [navigate]);

  const handleSaveMarketplace = useCallback(
    async (intent: 'draft' | 'publish' = 'draft') => {
      const data = marketplaceFormMethods.getValues();

      try {
        if (isEditing) {
          if (isPublished) {
            await updatePublishedProduct(data as MarketplaceRootData);
          } else {
            await updateMarketplaceProduct(data as MarketplaceRootData);

            if (intent === 'publish' && data.schedule.publishType === 'now') {
              await activateMarketplaceProduct();
            }
          }
        } else {
          const missingFields: string[] = [];
          if (!data.creation?.productType)
            missingFields.push('Tipo do produto');
          if (!data.creation?.title) missingFields.push('Nome do produto');

          if (missingFields.length > 0) {
            Modal.error({
              title: 'Campos obrigatórios não preenchidos',
              content: `Preencha os seguintes campos antes de salvar um novo item: ${missingFields.join(', ')}.`,
              okText: 'Voltar ao formulário',
              centered: true,
              ...baseProps,
            });
            return false;
          }

          const { marketplaceId } = await createMarketplaceProduct(
            data as MarketplaceRootData
          );
          marketplaceFormMethods.reset(data);
          setShowUnsavedAlert(false);
          skipBlockerRef.current = true;
          navigate(`/marketplace/create-or-update/${marketplaceId}`, {
            replace: true,
          });
        }

        if (isEditing) {
          marketplaceFormMethods.reset(data);
          setShowUnsavedAlert(false);
        }
        message.success(getSuccessMessage(intent, data.schedule?.publishType));
        if (intent === 'publish') {
          skipBlockerRef.current = true;
          navigate('/marketplace', { replace: true });
        }
        return true;
      } catch (error) {
        message.error('Erro ao salvar os dados. Tente novamente.');
        return false;
      }
    },
    [
      createMarketplaceProduct,
      activateMarketplaceProduct,
      isEditing,
      isPublished,
      marketplaceFormMethods,
      navigate,
      updateMarketplaceProduct,
      updatePublishedProduct,
    ]
  );

  return {
    isEditing,
    isPublished,
    isLoadingProduct,
    headerInfo,
    marketplaceFormMethods,
    marketplaceId,
    handleSaveMarketplace,
    handleBack,
    isLoadingSubmit,
    isPublishActionDisabled,
    currentStep,
    setCurrentStep: handleChangeStep,
    stepsCompleted,
    stepsWithErrors,
    publishActionLabel,
    showUnsavedAlert,
    productStatus,
  };
}
